import { useCallback, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Chip,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { format, parseISO } from "date-fns";
import {
  useGetBenefitCategoriesQuery,
  useGetBenefitServicesQuery,
  useLazyGetBenefitServiceQuery,
  useCreatePartnerBenefitServiceMutation,
  useUpdatePartnerBenefitServiceMutation,
  useDeleteBenefitServiceMutation,
  useApproveBenefitServiceMutation,
  useRejectBenefitServiceMutation,
  BenefitService,
  BenefitServiceListItem,
  BenefitServiceStatus,
  BenefitDiscountType,
  BENEFIT_DISCOUNT_TYPE_LABELS,
  BENEFIT_SERVICE_STATUS_LABELS,
  PartnerBenefitServiceBody,
  RejectBenefitServiceBody,
  GetBenefitServicesParams,
} from "../../../entities/admin-benefits";
import { useGetCitiesQuery } from "../../../entities/admin-city";
import { useGetMyPermissionsQuery } from "../../../entities/me";
import { ADMIN_PERMISSION_BENEFITS } from "../../../shared/constants/admin-permission-ids";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";
import { BenefitsRejectDialog } from "./benefits-reject-dialog";
import { BenefitsServiceViewDialog } from "./benefits-service-view-dialog";
import { BenefitsPartnerFormDialog } from "./benefits-partner-form-dialog";

type BenefitsTab = "moderation" | "services";

const STATUS_OPTIONS: { value: BenefitServiceStatus | ""; label: string }[] = [
  { value: "", label: "Все статусы" },
  { value: "pendingReview", label: BENEFIT_SERVICE_STATUS_LABELS.pendingReview },
  { value: "active", label: BENEFIT_SERVICE_STATUS_LABELS.active },
  { value: "needsRevision", label: BENEFIT_SERVICE_STATUS_LABELS.needsRevision },
  { value: "inactive", label: BENEFIT_SERVICE_STATUS_LABELS.inactive },
  {
    value: "ownerNotResident",
    label: BENEFIT_SERVICE_STATUS_LABELS.ownerNotResident,
  },
];

const formatListDate = (value?: string) => {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd.MM.yyyy");
  } catch {
    return value;
  }
};

const formatListDiscount = (item: BenefitServiceListItem) => {
  const label = BENEFIT_DISCOUNT_TYPE_LABELS[item.discountType];
  if (item.discountType === "percent") return `${item.discountValue}%`;
  if (item.discountType === "fixedAmount") return `${item.discountValue}`;
  return label;
};

export const BenefitsManagement = () => {
  const [activeTab, setActiveTab] = useState<BenefitsTab>("moderation");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<BenefitServiceStatus | "">(
    "",
  );
  const [partnerFilter, setPartnerFilter] = useState<"" | "true" | "false">("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [discountFilter, setDiscountFilter] = useState<BenefitDiscountType | "">(
    "",
  );
  const [hasPromoFilter, setHasPromoFilter] = useState<"" | "true" | "false">(
    "",
  );
  const [sort, setSort] = useState<string>("created");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [pageTokenHistory, setPageTokenHistory] = useState<
    (string | undefined)[]
  >([]);
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(
    undefined,
  );

  const [viewService, setViewService] = useState<BenefitService | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openPartnerForm, setOpenPartnerForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState<BenefitService | null>(
    null,
  );
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState<number | null>(null);
  const [rejectTargetTitle, setRejectTargetTitle] = useState("");

  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useGetMyPermissionsQuery();
  const canAccessBenefits =
    permissionsData?.permissions?.includes(ADMIN_PERMISSION_BENEFITS) ?? false;

  const { data: categoriesData } = useGetBenefitCategoriesQuery(undefined, {
    skip: !canAccessBenefits,
  });
  const { data: citiesData } = useGetCitiesQuery(undefined, {
    skip: !canAccessBenefits,
  });

  const queryParams = useMemo(() => {
    if (activeTab === "moderation") {
      return {
        status: "pendingReview" as BenefitServiceStatus,
        partner: false,
        search: search || undefined,
        sort: sort as GetBenefitServicesParams["sort"],
        order,
        pageToken: currentPageToken,
        limit,
      };
    }
    return {
      status: statusFilter || undefined,
      partner:
        partnerFilter === ""
          ? undefined
          : partnerFilter === "true",
      categoryId:
        categoryFilter === "" ? undefined : Number(categoryFilter),
      discountType: discountFilter || undefined,
      hasPromoCode:
        hasPromoFilter === ""
          ? undefined
          : hasPromoFilter === "true",
      search: search || undefined,
      sort: sort as GetBenefitServicesParams["sort"],
      order,
      pageToken: currentPageToken,
      limit,
    };
  }, [
    activeTab,
    search,
    statusFilter,
    partnerFilter,
    categoryFilter,
    discountFilter,
    hasPromoFilter,
    sort,
    order,
    currentPageToken,
    limit,
  ]);

  const { data, isLoading, refetch } = useGetBenefitServicesQuery(queryParams, {
    skip: isLoadingPermissions || !canAccessBenefits,
  });

  const [fetchService, { isFetching: isFetchingService }] =
    useLazyGetBenefitServiceQuery();
  const [createPartner, { isLoading: isCreating }] =
    useCreatePartnerBenefitServiceMutation();
  const [updatePartner, { isLoading: isUpdating }] =
    useUpdatePartnerBenefitServiceMutation();
  const [deleteService] = useDeleteBenefitServiceMutation();
  const [approveService, { isLoading: isApproving }] =
    useApproveBenefitServiceMutation();
  const [rejectService, { isLoading: isRejecting }] =
    useRejectBenefitServiceMutation();

  const services = data?.services ?? [];
  const nextPageToken = data?.pageToken?.trim() || undefined;
  const categories = categoriesData?.categories ?? [];
  const cities = useMemo(() => citiesData?.cities ?? [], [citiesData?.cities]);

  const cityNames = useMemo(
    () =>
      cities.reduce<Record<number, string>>((acc, city) => {
        acc[city.id] = city.name;
        return acc;
      }, {}),
    [cities],
  );

  const resetPagination = useCallback(() => {
    setPage(0);
    setPageTokenHistory([]);
    setCurrentPageToken(undefined);
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, value: BenefitsTab) => {
    setActiveTab(value);
    resetPagination();
  };

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => {
      if (newPage > page) {
        if (nextPageToken) {
          setPageTokenHistory((prev) => [...prev, currentPageToken]);
          setCurrentPageToken(nextPageToken);
          setPage(newPage);
        }
      } else if (newPage < page) {
        const prev = [...pageTokenHistory];
        const lastPageToken = prev.pop();
        setPageTokenHistory(prev);
        setCurrentPageToken(lastPageToken);
        setPage(newPage);
      }
    },
    [page, nextPageToken, currentPageToken, pageTokenHistory],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLimit(parseInt(event.target.value, 10));
      resetPagination();
    },
    [resetPagination],
  );

  const openServiceDetail = async (id: number) => {
    setOpenViewDialog(true);
    setViewService(null);
    try {
      const result = await fetchService(id).unwrap();
      setViewService(result.service);
    } catch {
      alert("Не удалось загрузить услугу");
      setOpenViewDialog(false);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await approveService(id).unwrap();
      refetch();
      if (viewService?.id === id) {
        const result = await fetchService(id).unwrap();
        setViewService(result.service);
      }
    } catch {
      alert("Не удалось одобрить услугу");
    }
  };

  const handleRejectSubmit = async (body: RejectBenefitServiceBody) => {
    if (rejectTargetId == null) return;
    try {
      await rejectService({ id: rejectTargetId, body }).unwrap();
      setOpenRejectDialog(false);
      setRejectTargetId(null);
      refetch();
      if (viewService?.id === rejectTargetId) {
        const result = await fetchService(rejectTargetId).unwrap();
        setViewService(result.service);
      }
    } catch {
      alert("Не удалось отклонить услугу");
    }
  };

  const openReject = (id: number, title: string) => {
    setRejectTargetId(id);
    setRejectTargetTitle(title);
    setOpenRejectDialog(true);
  };

  const handlePartnerSubmit = async (body: PartnerBenefitServiceBody) => {
    try {
      if (editingPartner) {
        await updatePartner({ id: editingPartner.id, body }).unwrap();
      } else {
        await createPartner(body).unwrap();
      }
      setOpenPartnerForm(false);
      setEditingPartner(null);
      refetch();
    } catch {
      alert("Не удалось сохранить услугу");
    }
  };

  const handleDeletePartner = async (id: number) => {
    if (!confirm("Удалить партнёрскую услугу?")) return;
    try {
      await deleteService(id).unwrap();
      setOpenViewDialog(false);
      setViewService(null);
      refetch();
    } catch {
      alert("Не удалось удалить услугу");
    }
  };

  if (isLoadingPermissions) {
    return <LoadingIndicator />;
  }

  if (!canAccessBenefits) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography>Нет доступа к разделу RAPL Benefits</Typography>
      </Container>
    );
  }

  const isModerating = isApproving || isRejecting;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">RAPL Benefits — Услуги</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingPartner(null);
            setOpenPartnerForm(true);
          }}
        >
          Партнёрская услуга
        </Button>
      </Stack>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Очередь модерации" value="moderation" />
        <Tab label="Все услуги" value="services" />
      </Tabs>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing="10px">
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Поиск по названию или описанию"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetPagination();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260, flex: 1 }}
            />

            {activeTab === "services" && (
              <>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Статус</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Статус"
                    onChange={(e) => {
                      setStatusFilter(e.target.value as BenefitServiceStatus | "");
                      resetPagination();
                    }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Тип</InputLabel>
                  <Select
                    value={partnerFilter}
                    label="Тип"
                    onChange={(e) => {
                      setPartnerFilter(e.target.value as "" | "true" | "false");
                      resetPagination();
                    }}
                  >
                    <MenuItem value="">Все</MenuItem>
                    <MenuItem value="false">Резидентские</MenuItem>
                    <MenuItem value="true">Партнёрские</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Категория</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Категория"
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      resetPagination();
                    }}
                  >
                    <MenuItem value="">Все</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Скидка</InputLabel>
                  <Select
                    value={discountFilter}
                    label="Скидка"
                    onChange={(e) => {
                      setDiscountFilter(e.target.value as BenefitDiscountType | "");
                      resetPagination();
                    }}
                  >
                    <MenuItem value="">Все</MenuItem>
                    {(
                      Object.keys(
                        BENEFIT_DISCOUNT_TYPE_LABELS,
                      ) as BenefitDiscountType[]
                    ).map((type) => (
                      <MenuItem key={type} value={type}>
                        {BENEFIT_DISCOUNT_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
            {activeTab === "services" && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Промокод</InputLabel>
                <Select
                  value={hasPromoFilter}
                  label="Промокод"
                  onChange={(e) => {
                    setHasPromoFilter(e.target.value as "" | "true" | "false");
                    resetPagination();
                  }}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="true">С промокодом</MenuItem>
                  <MenuItem value="false">Без промокода</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Сортировка</InputLabel>
              <Select
                value={sort}
                label="Сортировка"
                onChange={(e) => {
                  setSort(e.target.value);
                  resetPagination();
                }}
              >
                <MenuItem value="created">Создание</MenuItem>
                <MenuItem value="updated">Обновление</MenuItem>
                <MenuItem value="title">Название</MenuItem>
                <MenuItem value="expires">Срок действия</MenuItem>
                <MenuItem value="published">Публикация</MenuItem>
                <MenuItem value="partner">Тип</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Порядок</InputLabel>
              <Select
                value={order}
                label="Порядок"
                onChange={(e) => {
                  setOrder(e.target.value as "asc" | "desc");
                  resetPagination();
                }}
              >
                <MenuItem value="desc">По убыванию</MenuItem>
                <MenuItem value="asc">По возрастанию</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Тип</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Категория</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Скидка</TableCell>
                  <TableCell>Владелец / партнёр</TableCell>
                  <TableCell>Создано</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Нет услуг
                    </TableCell>
                  </TableRow>
                ) : (
                  services.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.isPartner ? "Партнёр" : "Резидент"}
                          color={item.isPartner ? "primary" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {item.photoUrl && (
                            <Avatar
                              src={item.photoUrl}
                              variant="rounded"
                              sx={{ width: 36, height: 36 }}
                            />
                          )}
                          <Typography variant="body2">{item.title}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{item.categoryName}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={BENEFIT_SERVICE_STATUS_LABELS[item.status]}
                        />
                      </TableCell>
                      <TableCell>{formatListDiscount(item)}</TableCell>
                      <TableCell>
                        {item.isPartner
                          ? item.partnerName
                          : item.owner
                            ? `${item.owner.firstName} ${item.owner.lastName}`
                            : "—"}
                      </TableCell>
                      <TableCell>{formatListDate(item.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          <Tooltip title="Просмотр">
                            <IconButton
                              size="small"
                              onClick={() => openServiceDetail(item.id)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {item.isPartner && (
                            <Tooltip title="Редактировать">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  const result = await fetchService(
                                    item.id,
                                  ).unwrap();
                                  setEditingPartner(result.service);
                                  setOpenPartnerForm(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!item.isPartner &&
                            item.status === "pendingReview" && (
                              <>
                                <Tooltip title="Одобрить">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={isModerating}
                                    onClick={() => handleApprove(item.id)}
                                  >
                                    <ThumbUpIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Отклонить">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={isModerating}
                                    onClick={() =>
                                      openReject(item.id, item.title)
                                    }
                                  >
                                    <ThumbDownIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={-1}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            labelDisplayedRows={({ from, to }) => `${from}–${to}`}
            nextIconButtonProps={{ disabled: !nextPageToken }}
            backIconButtonProps={{ disabled: page === 0 }}
          />
        </>
      )}

      <BenefitsServiceViewDialog
        open={openViewDialog}
        service={viewService}
        cityNames={cityNames}
        isLoading={isFetchingService}
        isModerating={isModerating}
        onClose={() => {
          setOpenViewDialog(false);
          setViewService(null);
        }}
        onApprove={
          viewService && !viewService.isPartner
            ? () => handleApprove(viewService.id)
            : undefined
        }
        onReject={
          viewService && !viewService.isPartner
            ? () => openReject(viewService.id, viewService.title)
            : undefined
        }
        onEditPartner={
          viewService?.isPartner
            ? () => {
                setEditingPartner(viewService);
                setOpenPartnerForm(true);
              }
            : undefined
        }
        onDeletePartner={
          viewService?.isPartner
            ? () => handleDeletePartner(viewService.id)
            : undefined
        }
      />

      <BenefitsPartnerFormDialog
        open={openPartnerForm}
        service={editingPartner}
        categories={categories}
        cities={cities}
        isSubmitting={isCreating || isUpdating}
        onClose={() => {
          setOpenPartnerForm(false);
          setEditingPartner(null);
        }}
        onSubmit={handlePartnerSubmit}
      />

      <BenefitsRejectDialog
        open={openRejectDialog}
        serviceTitle={rejectTargetTitle}
        isSubmitting={isRejecting}
        onClose={() => {
          setOpenRejectDialog(false);
          setRejectTargetId(null);
        }}
        onSubmit={handleRejectSubmit}
      />
    </Container>
  );
};
